import { Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useCountryConfig,
  type CountryConfig,
} from '@/lib/countryConfig';
import { splitE164, usePhoneCountryConfigs } from '@/lib/phoneCountries';

/**
 * Phone field: country-code dropdown + national number.
 *
 * The country list comes from the admin-managed operational countries, NOT a
 * generic ~240-entry list. A generic list is actively wrong here: mobile money
 * only reaches lines in markets the platform actually operates in, so offering
 * a prefix from a market we do not serve invites a number no provider can
 * charge. Sourcing from operational countries also closes the divergence
 * issue #465 flags — operational countries and phone-prefix validation were
 * two separate lists that could disagree.
 *
 * This is also why no phone library is used. libphonenumber-js and
 * react-phone-number-input both assume the full ITU country list and bring
 * their own validity rules; countryConfig already carries phonePrefix,
 * phoneNationalDigits and localPhonePattern per country, resolved from the
 * database. Reusing it keeps one source of truth and avoids a ~30kB addition
 * to a bundle that is already gated on size.
 *
 * `value` and `onChange` speak E.164 — the storage format — while the visible
 * input holds only national digits.
 */

interface PhoneInputProps {
  /** E.164 international form. Empty string when unset. */
  value: string;
  /** Receives E.164, or "" when the national part is cleared. */
  onChange: (e164: string) => void;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Overrides the placeholder derived from the selected country. */
  placeholder?: string;
  className?: string;
  autoComplete?: string;
  onBlur?: () => void;
  /**
   * Country whose prefix this field starts on while `value` is empty.
   *
   * Without it the field falls back to the PLATFORM's primary market, which is
   * wrong wherever the thing being paid for belongs to a different one: a
   * cross-market booking opened on the platform's prefix, and a national number
   * typed into it validated and persisted under the WRONG market's E.164 form —
   * which is the number mobile money is then sent to. Callers that know the
   * market (a booking's country_code) must say so.
   *
   * A non-empty `value` still wins: the prefix already in the data decides.
   */
  countryCode?: string;
}

export function PhoneInput({
  value,
  onChange,
  id,
  disabled,
  invalid,
  placeholder,
  className,
  autoComplete = 'tel',
  onBlur,
  countryCode,
}: PhoneInputProps) {
  const { config: activeConfig } = useCountryConfig();
  const configs = usePhoneCountryConfigs();

  const { config: parsedConfig, national } = splitE164(value, configs);
  // Precedence: the prefix already in the value, then the market the caller
  // named, then the platform's own. The platform default is last because it is
  // only ever a guess about which market this number belongs to.
  const selected =
    parsedConfig ??
    (countryCode ? configs.find((c) => c.code === countryCode) : undefined) ??
    configs.find((c) => c.code === activeConfig.code) ??
    configs[0] ??
    activeConfig;

  const emit = (nationalDigits: string, cfg: CountryConfig) => {
    const digits = nationalDigits.replace(/\D/g, '');
    // A leading 0 is how these numbers are written locally but is not part of
    // the international form — drop it rather than reject it.
    const withoutTrunk = digits.replace(/^0+/, '');
    const capped = withoutTrunk.slice(0, cfg.phoneNationalDigits);
    onChange(capped ? `${cfg.phonePrefix}${capped}` : '');
  };

  // Strip the dialling prefix from the sample so the hint matches what the
  // user actually types into this input.
  const nationalPlaceholder =
    placeholder ??
    selected.phonePlaceholder.replace(selected.phonePrefix, '').trim();

  return (
    <div className={cn('flex min-w-0 gap-2', className)}>
      <Select
        value={selected.code}
        disabled={disabled}
        onValueChange={(code) => {
          const next = configs.find((c) => c.code === code);
          if (next) emit(national, next);
        }}
      >
        {/* Sized to its content, not a fixed 7.5rem. On iOS the flag emoji is
            wide enough that the flag + dialling code overflowed the fixed width, and the
            trigger's line-clamp then drew an ellipsis ("..") between the flag
            and the code. */}
        <SelectTrigger
          className="w-auto min-w-[7.5rem] shrink-0 gap-1.5 [&>span]:line-clamp-none"
          aria-label="Country dialling code"
        >
          <SelectValue>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span aria-hidden="true">{selected.flagEmoji}</span>
              <span className="tabular-nums">{selected.phonePrefix}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {configs.map((c) => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span aria-hidden="true">{c.flagEmoji}</span>
                <span>{c.name}</span>
                <span className="text-muted-foreground tabular-nums">{c.phonePrefix}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative min-w-0 flex-1">
        <Phone
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          disabled={disabled}
          placeholder={nationalPlaceholder}
          value={national}
          onChange={(e) => emit(e.target.value, selected)}
          onBlur={onBlur}
          aria-invalid={invalid || undefined}
          className={cn(
            'pl-10 min-w-0',
            invalid && 'border-destructive focus-visible:ring-destructive',
          )}
        />
      </div>
    </div>
  );
}

export default PhoneInput;
